import {useEffect, useState} from "react"

// External libraries
import {useQuery} from "@apollo/client"
import {useInView} from "react-intersection-observer"

// Internal hooks and contexts
import {useUser} from "@/contexts/UserContextProvider"
import {useArchiveSearch} from "@/features/archive-browser/hooks/useArchiveSearch"
import {useGetNavigationThreads} from "@/utils/graphQL/queries/threads/useGetNavigationThreads"

// Components
import Indicator from "@/components/archive/Indicator"
import {DataLoader} from "@/components/general/ui/DataLoader"
import {StubIndicator} from "@/components/common/StubIndicator"
import {
  ExportDataModal,
  FilterBar,
  SearchBar,
  ThreadList,
  VIEW_MODES,
} from "@/features/archive-browser"
import {ThreadDrawer} from "@/features/thread-drawer"

// Constants
import {ITEMS_PER_PAGE} from "@/constants"

// GraphQL
import {GetCountOrgUsersDocument} from "@/gql/graphql"

// Stub data functionality
import {isStubEnabled} from "@/config/stubConfig"
import {useStubNavigationThreads} from "@/utils/hooks/useStubThreads"

// Define if we should use stub data for threads
export const USE_STUB_DATA = isStubEnabled("threads")

// Phone number formatting - commented out but kept for future reference
// const quartzNumber = process.env.REACT_APP_ORG_PHONE_NUMBER
// function convertToQuartzFormat(phoneNumber: string) {
//   // converts number xxxxxxxxxx to +1 (xxx) xxx-xxxx
//   return `+1 (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`
// }

function ArchivePage() {
  const {session, orgId} = useUser()

  // Infinite scroll detection
  const {ref: loadMoreRef, inView: loadMoreInView} = useInView({
    threshold: 0,
    triggerOnce: false,
  })

  const {data: orgUsers} = useQuery(GetCountOrgUsersDocument)
  const numberOfUsers = orgUsers?.org_usersCollection?.edges?.length

  // Search and filtering
  const {
    searchTerm,
    setSearchTerm,
    view,
    filters,
    searchThreads,
    searchLoading,
    searchHasMore,
    handleFiltersChange,
    handleClearAllFilters,
    loadMoreSearchResults,
  } = useArchiveSearch()

  // Always fetch both real and stub data to maintain consistent hook ordering
  const stubNavThreads = useStubNavigationThreads(ITEMS_PER_PAGE)
  const realNavThreads = useGetNavigationThreads(ITEMS_PER_PAGE)

  // Use either stub or real data based on configuration
  const {loading, error, monthThreads, hasNextPage, loadMore} = USE_STUB_DATA
    ? stubNavThreads
    : realNavThreads

  // Handle infinite scrolling for month view
  useEffect(() => {
    if (
      loadMoreInView &&
      hasNextPage &&
      !loading &&
      view === VIEW_MODES.MONTH_VIEW
    ) {
      loadMore()
    }
  }, [loadMoreInView, hasNextPage, loading, loadMore, view])

  // Handle infinite scrolling for search results with debounce to prevent multiple triggers
  useEffect(() => {
    if (
      loadMoreInView &&
      searchHasMore &&
      !searchLoading &&
      view === VIEW_MODES.SEARCH_RESULTS
    ) {
      // Small delay to prevent multiple triggers
      const timer = setTimeout(() => {
        loadMoreSearchResults()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [
    loadMoreInView,
    searchHasMore,
    searchLoading,
    view,
    loadMoreSearchResults,
  ])

  return (
    <div className="animate-fade animate-once animate-ease-linear mr-4 flex w-full grid-cols-7 justify-between gap-4 rounded-md border-black/5 bg-onyx-white p-4  transition-all duration-150">
      {/* Thread list panel */}
      <div className="col-span-4 flex h-full w-full min-w-[30%] flex-col justify-between overflow-auto bg-white">
        {/* Sticky header with controls */}
        <div className="sticky top-0 z-50 flex flex-col justify-start gap-2 bg-white">
          <div className="z-50 flex w-full flex-row items-center justify-between gap-2 bg-white">
            <div className="flex flex-col gap-2 bg-white">
              {/* <Indicator
                isProcessed={true}
                type="status"
                label="Dedicated Quartz Number"
                text={convertToQuartzFormat(quartzNumber)}
              /> */}
              <Indicator
                isProcessed={true}
                type="status"
                label={`${numberOfUsers} Whitelisted Users`}
              />
              <StubIndicator featureName="threads" show={USE_STUB_DATA} />
            </div>
            <ExportDataModal />
          </div>
          <FilterBar
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearAll={handleClearAllFilters}
          />
          <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
        </div>
        {/* Scrollable thread list */}
        <div className="scroll-container h-full min-h-[80%] overflow-auto pb-4">
          {loading && !monthThreads.size && <DataLoader />}

          <ThreadList
            monthThreads={
              view === VIEW_MODES.MONTH_VIEW ? monthThreads : undefined
            }
            searchThreads={
              view === VIEW_MODES.SEARCH_RESULTS ? searchThreads : undefined
            }
            mode={view}
            isLoading={view === VIEW_MODES.MONTH_VIEW ? loading : searchLoading}
            hasNextPage={
              view === VIEW_MODES.MONTH_VIEW ? hasNextPage : searchHasMore
            }
            loadMoreRef={loadMoreRef}
          />
        </div>
      </div>

      {/* Thread drawer panel - replaces EmailView */}
      <ThreadDrawer />
    </div>
  )
}

export default ArchivePage
